import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import FilePreviewModal from "@/components/FilePreviewModal";
import { CheckCircle2, Eye, FileText, Loader2, PenLine, Trash2, RefreshCw, History } from "lucide-react";
import RevisionHistoryDialog from "./RevisionHistoryDialog";
import { TYPE_META, fmtDate } from "./assessmentMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AssessmentList({ items, canEdit, showClient = false, onOpen, onChanged, emptyText }) {
  const [preview, setPreview] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [history, setHistory] = useState(null);

  const revise = async (a) => {
    setBusyId(a.id);
    try {
      const r = await axios.post(`${API}/assessments/${a.id}/revise`);
      toast.success(`${a.template_title} — ${r.data.revision_label || `Update ${r.data.revision_number}`} started. Earlier signatures stay locked; sign and date this visit's row.`);
      onChanged?.();
      onOpen?.(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start the update");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete this ${a.type_label} draft?`)) return;
    setBusyId(a.id);
    try {
      await axios.delete(`${API}/assessments/${a.id}`);
      toast.success("Draft deleted");
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete");
    } finally {
      setBusyId(null);
    }
  };

  const latestRev = new Map(); // root id -> highest revision number present, so only the newest version offers "Update"
  for (const a of items || []) {
    if (!a.recurring_count) continue;
    const root = a.root_id || a.id;
    latestRev.set(root, Math.max(latestRev.get(root) ?? -1, a.revision_number || 0));
  }
  const isLatest = (a) => (latestRev.get(a.root_id || a.id) ?? 0) === (a.revision_number || 0);

  const groups = [];
  const byKey = new Map();
  for (const a of items || []) {
    const key = a.visit_group_id || a.id;
    if (!byKey.has(key)) { const g = { key, forms: [] }; byKey.set(key, g); groups.push(g); }
    byKey.get(key).forms.push(a);
  }

  if (items === null) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500" data-testid="assessments-empty">
        <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
        <p className="text-sm">{emptyText || "No assessments yet."}</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        {groups.map((g) => {
          const a = g.forms[0];
          const meta = TYPE_META[a.assessment_type] || {};
          const Icon = meta.icon || FileText;
          const doneCount = g.forms.filter((f) => f.status === "completed").length;
          const allDone = doneCount === g.forms.length;
          const single = g.forms.length === 1;
          const actions = (f) => (
            f.status === "completed" ? (
              <>
                <Button size="sm" variant="outline" className="gap-1" onClick={() => setPreview(f)} data-testid={`assessment-view-${f.id}`}>
                  <Eye className="w-3.5 h-3.5" /> View PDF
                </Button>
                {f.recurring_count > 0 && (
                  <Button size="sm" variant="ghost" className="gap-1 text-sky-700" onClick={() => setHistory(f)} data-testid={`assessment-history-${f.id}`}>
                    <History className="w-3.5 h-3.5" /> Versions
                  </Button>
                )}
                {canEdit && f.recurring_count > 0 && (f.revision_number || 0) < f.recurring_count && isLatest(f) && !f.superseded && (
                  <Button size="sm" className="gap-1 bg-sky-600 hover:bg-sky-700" disabled={busyId === f.id} onClick={() => revise(f)} title="Start the next supervisory-visit update: content carries over, this visit's signature/date row becomes live" data-testid={`assessment-revise-${f.id}`}>
                    {busyId === f.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Update for supervisory visit
                  </Button>
                )}
              </>
            ) : canEdit ? (
              <>
                <Button size="sm" className="gap-1 bg-rose-600 hover:bg-rose-700" onClick={() => onOpen(f)} data-testid={`assessment-continue-${f.id}`}>
                  <PenLine className="w-3.5 h-3.5" /> Continue
                </Button>
                <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50" disabled={busyId === f.id} onClick={() => remove(f)} data-testid={`assessment-delete-${f.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </>
            ) : (
              <Badge variant="outline" className="text-[10px]">In progress</Badge>
            )
          );
          return (
            <div key={g.key} className="rounded-xl border border-slate-200 bg-white p-3 sm:p-4" data-testid={`assessment-row-${a.id}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className={`h-9 w-9 rounded-lg border flex items-center justify-center shrink-0 ${meta.color || "bg-slate-100"}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-slate-900">{single && meta.paperwork ? a.template_title : a.type_label}{!single && <span className="text-slate-500 font-normal"> — {fmtDate(a.visit_date)}</span>}</p>
                    {single && meta.paperwork && <Badge variant="outline" className={`text-[10px] ${meta.color}`}>Admission form</Badge>}
                    {single && a.recurring_count > 0 && (
                      <Badge className="bg-sky-100 text-sky-800 border-sky-200 text-[10px]" data-testid={`assessment-revision-${a.id}`}>
                        {a.revision_label || "Original"}{a.superseded ? " · replaced by new cycle" : (a.revision_number || 0) >= a.recurring_count ? " · cycle complete" : ""}
                      </Badge>
                    )}
                    {allDone ? (
                      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 gap-1 text-[10px]" data-testid={`assessment-status-${a.id}`}><CheckCircle2 className="w-3 h-3" /> {single ? "Completed" : "Visit completed"}</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[10px]" data-testid={`assessment-status-${a.id}`}>{single ? "Draft" : `${doneCount} of ${g.forms.length} forms completed`}</Badge>
                    )}
                    {g.forms.some((f) => f.referral_id) && <Badge variant="outline" className="text-[10px]">Referral updated</Badge>}
                    {g.forms.some((f) => f.clinical_summary) && <Badge variant="outline" className="text-[10px] text-purple-700 border-purple-200">CMS-485 summary</Badge>}
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {showClient && a.client_name && (
                      <><Link to={`/clients/${a.client_id}?tab=assessments`} className="text-slate-700 hover:underline font-medium">{a.client_name}</Link> · </>
                    )}
                    Visit {fmtDate(a.visit_date)} · {a.rn_name}{single && <> · {a.template_title}</>}
                    {single && a.status === "completed" && a.completed_at && <> · completed {new Date(a.completed_at).toLocaleDateString()}</>}
                    {single && a.status !== "completed" && a.updated_at && <> · last saved {new Date(a.updated_at).toLocaleString()}</>}
                  </p>
                </div>
                {single && <div className="flex items-center gap-1.5">{actions(a)}</div>}
              </div>
              {!single && (
                <div className="mt-3 ml-0 sm:ml-12 divide-y divide-slate-100 rounded-lg border border-slate-100" data-testid={`assessment-group-forms-${a.id}`}>
                  {g.forms.map((f) => (
                    <div key={f.id} className="flex flex-wrap items-center gap-2 px-3 py-2" data-testid={`assessment-form-row-${f.id}`}>
                      {f.status === "completed" ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <PenLine className="w-4 h-4 text-amber-500 shrink-0" />}
                      <span className="flex-1 min-w-[160px] text-sm text-slate-800 truncate">{f.template_title}{f.recurring_count > 0 && <span className="ml-1 text-[10px] rounded bg-sky-100 text-sky-800 px-1" data-testid={`assessment-revision-${f.id}`}>{f.revision_label || "Original"}</span>}</span>
                      <span className="text-[11px] text-slate-400 hidden sm:inline">{f.status === "completed" && f.completed_at ? `completed ${new Date(f.completed_at).toLocaleDateString()}` : f.updated_at ? `saved ${new Date(f.updated_at).toLocaleString()}` : ""}</span>
                      <div className="flex items-center gap-1.5">{actions(f)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {history && <RevisionHistoryDialog assessment={history} onClose={() => setHistory(null)} onView={(v) => { setHistory(null); setPreview({ ...history, ...v }); }} />}
      {preview && (
        <FilePreviewModal
          open
          onClose={() => setPreview(null)}
          file={{ id: preview.id, filename: `RN ${preview.type_label} Assessment - ${preview.client_name} - ${preview.visit_date}.pdf`, content_type: "application/pdf" }}
          url={`${API}/assessments/${preview.id}/pdf`}
        />
      )}
    </>
  );
}
